import React, { useState, useEffect } from 'react'
import { Link, useParams, useHistory } from 'react-router-dom'
import Select, { ValueType } from 'react-select'
import isObjEqual from 'fast-deep-equal'
import { ToastContainer, toast} from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import useSound from 'use-sound';

import { CardName, PlayingCard, Deck } from '../../Cards'
import { GameButton, Background, GameInfo, CardInfo, RivalOrderPanel, GameHeader, HeaderTitle, ActionBar, ActionPanel, SelectedCardsTable, WinningContainer, WinningCard, WinningTitle, WinningMessage, LobbyButton, MobilePlayerStrip } from '../styles'
import { useHistoryState } from '../../StateContext'
import Modal from '../Modal'
import LoadingIndicator from '../../components/LoadingIndicator'
import { CardSet, nullRoom, nullPlayer, Room } from '../../Types'
import {
    RemoveRoom,
    finishRound,
    makePlayerHeadOfOrder,
    RemovePlayer,
    getLastPlayedCardSet,
    getLastPlayedCardSetWithCards,
    moveCards
} from '../utils'
import { useSSE } from '../../hooks/useSSE'
import { apiClient } from '../../api/client'
import {
    allCardOptions,
    bluffOnClick,
    cardOptions,
    changeBorder,
    getCardNameFromSelect,
    OptionType, renderBluffCards, renderCards,
    renderInfo,
    renderSelectedCards,
    showOpponentHands
} from './gameUtils'
import Confetti from 'react-confetti'

// Strip the trailing question sentence from bluff outcome messages.
// Messages end with e.g. "Want to showcase their lies?" — we only want the statement part.
const stripQuestion = (msg: string): string => {
    const lastQ = msg.lastIndexOf('?')
    if (lastQ === -1) return msg
    const prevEnd = Math.max(
        msg.lastIndexOf('. ', lastQ),
        msg.lastIndexOf('! ', lastQ),
        msg.lastIndexOf('? ', lastQ)
    )
    if (prevEnd === -1) return msg  // whole message is a question, leave as-is
    return msg.slice(0, prevEnd + 1).trim()
}

const Game = () => {
    let { gameId } = useParams()
    const [room, setRoom] = useState<Room>(nullRoom)
    const [isTurn, setIsTurn] = useState<boolean>(false)
    const [currentPlayerId, setCurrentPlayerId] = useState<string>("")
    const [bluffCalled, setBluffCalled] = useState<boolean>(false)
    const [cards, setCards] = useState<PlayingCard[]>([])
    const [winner, setWinner] = useState<string>("")
    const [roundVal, setRoundVal] = useState<ValueType<OptionType>>({value: 'Joker', label:'Joker'})

    const [bluffModalIsOpen, setBluffModalIsOpen] = useState<boolean>(false)
    const [bluffModalText, setBluffModalText] = useState<React.ReactNode>("")
    const [controlBluffModalIsOpen, setControlBluffModalIsOpen] = useState<boolean>(false)

    const {state, setState} = useHistoryState()
    let history = useHistory()

    // sounds to notify what happened
    const [playIsTurnNotification] = useSound(`${process.env.PUBLIC_URL}/sounds/notificationSound.mp3`, {volume: 0.5});
    const [playSomeonePlayedSound] = useSound(`${process.env.PUBLIC_URL}/sounds/otherPlayerPlayed.mp3`, {volume: 0.5});

    const [selectedCards, setSelectedCards] = useState<boolean[]>([])
    const [numCardsSelected, setNumCardsSelected] = useState<number>(0)
    // defaults true
    const [showBluffToast, setShowBluffToast] = useState<boolean>(true)
    // defaults false
    const [showTurnToast, setShowTurnToast] = useState<boolean>(false)
    // Mobile-only: bottom sheet for picking round card value
    const [showMobileValuePicker, setShowMobileValuePicker] = useState<boolean>(false)

    // Persistent event banner — version increments on each new event to force re-animation even for same type
    const [eventBanner, setEventBanner] = useState<{type: 'bluff' | 'passes', message: string, version: number} | null>(null)

    // Static player order for the mobile strip — captured on first load, never reordered
    const [staticPlayerOrder, setStaticPlayerOrder] = useState<string[]>([])
    const hasSetStaticOrder = React.useRef(false)

    // Stable card IDs to prevent re-rendering when cards are played
    const cardIdCounter = React.useRef(0)
    const cardToIdMap = React.useRef(new Map<PlayingCard, string>())

    // Track previous showCardSetFromRound to detect when new cards are revealed
    const prevShowCardSetFromRound = React.useRef<boolean>(false)

    // Track previous roundEndedByPasses to detect pass notifications
    const prevRoundEndedByPasses = React.useRef<boolean>(false)

    // Track previous bluffCaller — when it transitions non-empty → empty, a bluff was resolved
    const prevBluffCaller = React.useRef<string>('')
    // Message from bluffOnClick outcome, stored so the banner can use it when "Don't show" is clicked
    const bluffOutcomeMessage = React.useRef<string>('')

    const username = state.username
    const player = room.order.find(p => p.playerId === username)

    // Track viewport width for responsive design
    const [viewportWidth, setViewportWidth] = useState(window.innerWidth)

    useEffect(() => {
        const handleResize = () => setViewportWidth(window.innerWidth)
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    // Check browser audio compatibility
    useEffect(() => {
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext
        if (!AudioContext) {
            toast.warning("Your browser may not support audio. Sounds may not work.", {
                autoClose: 5000,
                position: "bottom-right",
                toastId: "audioWarning"
            })
        }
    }, [])

    // Use SSE hook for real-time game updates
    const { room: sseRoom, isConnected, error: sseError } = useSSE({
        gameId,
        playerId: username,
        onUpdate: (newRoom) => {
            // Only update if room actually changed
            if (isObjEqual(room, newRoom)) {
                return
            }

            setRoom(newRoom)

            // Capture static player order once on first real room load (order stays fixed, highlight moves)
            if (!hasSetStaticOrder.current && newRoom.order.length > 0) {
                hasSetStaticOrder.current = true
                setStaticPlayerOrder(newRoom.order.map(p => p.playerId))
            }

            // Update player state if player exists
            const updatedPlayer = newRoom.order.find(p => p.playerId === username)
            if (updatedPlayer) {
                // Check if someone won
                for (let i = 0; i < newRoom.order.length; i++) {
                    if (newRoom.order[i].hand.deck.getCount() === 0 &&
                        ((i + 2) % newRoom.order.length === 0 || newRoom.cardsInPlay.getCount() === 0)) {
                        setWinner(newRoom.order[i].playerId)
                        break
                    }
                }

                // Show/hide bluff modal
                if (!newRoom.showCardSetFromRound) {
                    setControlBluffModalIsOpen(false)
                } else if (newRoom.showCardSetFromRound && !prevShowCardSetFromRound.current) {
                    // Cards are being revealed - reset control to allow modal to show
                    setControlBluffModalIsOpen(false)
                }
                prevShowCardSetFromRound.current = newRoom.showCardSetFromRound

                // Detect bluff resolution: bluffCaller transitions from non-empty → empty after moveCards()
                // The server never sets bluffStatus, so we detect via bluffCaller state change instead.
                const newBluffCallerId = newRoom.bluffCaller?.playerId || ''
                if (prevBluffCaller.current !== '' && newBluffCallerId === '') {
                    // Bluff was just resolved. For non-callers (who don't have bluffOutcomeMessage),
                    // show an informational banner. For the caller, the banner is set at the click site.
                    if (prevBluffCaller.current !== username) {
                        const bannerMsg = newRoom.showCardSetFromRound
                            ? `${prevBluffCaller.current} called bluff — cards revealed!`
                            : `${prevBluffCaller.current} called bluff — cards taken without being shown.`
                        setEventBanner(prev => ({ type: 'bluff', message: bannerMsg, version: (prev?.version ?? 0) + 1 }))
                    }
                }
                prevBluffCaller.current = newBluffCallerId

                // All-passes: set persistent banner (no toast — the banner is the notification)
                if (newRoom.roundEndedByPasses && !prevRoundEndedByPasses.current) {
                    setEventBanner(prev => ({
                        type: 'passes',
                        message: 'Everyone passed! A new round is beginning.',
                        version: (prev?.version ?? 0) + 1
                    }))
                }
                prevRoundEndedByPasses.current = newRoom.roundEndedByPasses

                // Update cards and preserve selected cards if hand didn't change
                const cardArr = updatedPlayer.hand.deck.getCards()

                // Assign stable IDs to new cards
                cardArr.forEach(card => {
                    if (!cardToIdMap.current.has(card)) {
                        const cardKey = `${card.cardName}-${card.suit}`
                        const existingId = Array.from(cardToIdMap.current.entries())
                            .find(([c, id]) => `${c.cardName}-${c.suit}` === cardKey && !cardArr.includes(c))

                        if (existingId) {
                            cardToIdMap.current.delete(existingId[0])
                            cardToIdMap.current.set(card, existingId[1])
                        } else {
                            cardToIdMap.current.set(card, `card-${cardIdCounter.current++}`)
                        }
                    }
                })

                // Clean up IDs for cards no longer in hand
                const currentCardSet = new Set(cardArr)
                Array.from(cardToIdMap.current.keys()).forEach(card => {
                    if (!currentCardSet.has(card)) {
                        cardToIdMap.current.delete(card)
                    }
                })

                // Check if the hand actually changed (different count or different cards)
                const handChanged = cardArr.length !== cards.length

                if (handChanged) {
                    // Hand changed (picked up cards, etc) - reset selection
                    setCards(cardArr)
                    const selCardArr = cardArr.map(() => false)
                    setSelectedCards(selCardArr)
                    setNumCardsSelected(0)
                } else {
                    // Hand unchanged - preserve selection but update cards reference
                    setCards(cardArr)
                    // Keep existing selectedCards and numCardsSelected
                }

                // Check if it's this player's turn
                const isPlayerTurn = newRoom.order[0].playerId === username
                setIsTurn(isPlayerTurn)

                // Play notification sounds
                if (currentPlayerId !== newRoom.order[0].playerId) {
                    setCurrentPlayerId(newRoom.order[0].playerId)

                    if (isPlayerTurn && showTurnToast) {
                        playIsTurnNotification()
                        setShowTurnToast(false)
                        if (!toast.isActive("isTurn")) {
                            toast.info("It's your turn!", {
                                toastId: "isTurn",
                                position: "top-center",
                                autoClose: 2000,
                                hideProgressBar: false,
                                closeOnClick: true,
                                pauseOnHover: true,
                                draggable: false,
                                className: "custom-turn-toast",
                                onClose: () => { setShowTurnToast(true) }
                            })
                        }
                    } else if (!isPlayerTurn) {
                        playSomeonePlayedSound()
                        setShowTurnToast(true)
                    }
                }

                // Update round value display
                if (newRoom.roundInfo.roundValue !== CardName.Joker) {
                    const roundValueOption = allCardOptions.find(
                        opt => opt.value === CardName[newRoom.roundInfo.roundValue]
                    )
                    if (roundValueOption) {
                        setRoundVal(roundValueOption)
                    }
                }
            }
        },
        onError: (err) => {
            console.error('SSE Error in Game:', err)
        }
    })

    // Extract play logic into reusable function
    const playSelectedCards = async () => {
        // Validate round value for first player
        let roundValue = undefined
        if (room.roundInfo.roundValue === CardName.Joker) {
            if (getCardNameFromSelect(roundVal) !== CardName.Joker) {
                roundValue = getCardNameFromSelect(roundVal)
            } else {
                if (!toast.isActive("dropdownInfo")) {
                    toast.error("You must choose a round value using the dropdown menu.", {
                        toastId: "dropdownInfo",
                    })
                }
                return
            }
        }

        // Check for bluff in progress
        if (room.bluffCaller.playerId !== "") {
            if (!toast.isActive("bluffInfo")) {
                toast.info("Someone has called bluff! You can't play until they choose to show the deck or keep it hidden.", {
                    toastId: "bluffInfo",
                    autoClose: 3000
                })
            }
            return
        }

        // Collect selected cards
        const cardArr: PlayingCard[] = []
        for (let i = 0; i < cards.length; i++) {
            if (selectedCards[i]) {
                cardArr.push(cards[i])
                changeBorder(document.getElementById(`card${i.toString()}`), false)
            }
        }

        if (cardArr.length === 0) {
            if (!toast.isActive("passInfo")) {
                toast.error("You haven't selected any cards to play? Did you want to pass?", {
                    toastId: "passInfo",
                })
            }
            return
        }

        // Call backend API to play cards
        try {
            await apiClient.playCards(gameId, username, cardArr, roundValue)
            // Reset selected cards state immediately
            setNumCardsSelected(0)
            setSelectedCards(cards.map(() => false))
            // SSE will update the room state automatically
        } catch (error) {
            console.error('Error playing cards:', error)
            toast.error('Failed to play cards. Please try again.')
        }
    }

    // Add keyboard listener for Enter key to play cards
    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            if (event.key !== 'Enter') return

            // Don't trigger if typing in input/select/textarea
            const activeElement = document.activeElement
            if (activeElement?.tagName === 'INPUT' ||
                activeElement?.tagName === 'SELECT' ||
                activeElement?.tagName === 'TEXTAREA') {
                return
            }

            // Check conditions to play
            const canPlay = isTurn &&
                           numCardsSelected > 0 &&
                           room.bluffCaller.playerId === "" &&
                           !isObjEqual(room, nullRoom)

            if (!canPlay) return

            event.preventDefault()
            playSelectedCards()
        }

        document.addEventListener('keydown', handleKeyPress)
        return () => document.removeEventListener('keydown', handleKeyPress)
    }, [isTurn, numCardsSelected, room, roundVal, cards, selectedCards, username, gameId])

    if (room.order.length === 1) {
        setWinner(room.order[0].playerId)
    }

    // add functionality for continuing later
    if (winner !== '' && player !== undefined) {
        setTimeout(()=>{RemoveRoom(room.gameId).then(()=>{setState({username: state.username, gameId: ""}); history.push("/")})}, 30000)
            if (winner === player.playerId) {
                return (
                    <Background>
                        <Confetti />
                        <Modal isOpen={room.showCardSetFromRound && !controlBluffModalIsOpen} buttonOnClick={()=>{setControlBluffModalIsOpen(true)}}>
                            {renderBluffCards(room.cardSetFromLastRound)}
                        </Modal>
                        <WinningContainer>
                            <WinningCard>
                                <WinningTitle>
                                    🎉 Victory! 🎉
                                </WinningTitle>
                                <WinningMessage>
                                    Congratulations! You've won the game!
                                </WinningMessage>
                                <WinningMessage style={{ fontSize: "16px", opacity: 0.8 }}>
                                    Returning to the title screen in 30 seconds...
                                </WinningMessage>
                                <Link
                                    onClick={()=>{setState({username: state.username, gameId: ""})}}
                                    to={{pathname: "/"}}
                                    style={{ width: "100%", textDecoration: "none" }}
                                >
                                    <LobbyButton width="100%">
                                        Back to Title Screen
                                    </LobbyButton>
                                </Link>
                            </WinningCard>
                        </WinningContainer>
                    </Background>
                )
            }
            return (
                <Background>
                    <Modal isOpen={room.showCardSetFromRound && !controlBluffModalIsOpen} buttonOnClick={()=>{setControlBluffModalIsOpen(true)}}>
                        {renderBluffCards(room.cardSetFromLastRound)}
                    </Modal>
                    <WinningContainer>
                        <WinningCard>
                            <WinningTitle>
                                Game Over
                            </WinningTitle>
                            <WinningMessage>
                                {winner} has won the game!
                            </WinningMessage>
                            <WinningMessage style={{ fontSize: "16px", opacity: 0.8 }}>
                                Returning to the title screen in 30 seconds...
                            </WinningMessage>
                            <Link
                                onClick={()=>{setState({username: state.username, gameId: ""})}}
                                to={{pathname: "/"}}
                                style={{ width: "100%", textDecoration: "none" }}
                            >
                                <LobbyButton width="100%">
                                    Back to Title Screen
                                </LobbyButton>
                            </Link>
                        </WinningCard>
                    </WinningContainer>
                </Background>
            )
    }
    
    if (bluffCalled) {
        setBluffCalled(false)
    }

    // Round finishing logic - backend handles this automatically now
    // The backend detects when everyone passes and finishes the round

    if (isObjEqual(room, nullRoom)) {
        return <LoadingIndicator message="Loading the game" />
    }

    return (
        <Background>
            <ToastContainer
                position={viewportWidth <= 768 ? "top-center" : "bottom-right"}
                style={viewportWidth <= 768 ? { top: "144px" } : {}}
            />

            {/* Animation keyframes */}
            <style>{`
                @keyframes bannerSlideIn {
                    from { opacity: 0; transform: translateY(8px) scale(0.97); }
                    to   { opacity: 1; transform: translateY(0)    scale(1);   }
                }
                .event-banner:hover { opacity: 0.88; }
                @keyframes pickValuePulse {
                    0%   { box-shadow: 0 0 0 0 rgba(251,191,36,0.6), 0 2px 8px rgba(0,0,0,0.3); }
                    60%  { box-shadow: 0 0 0 8px rgba(251,191,36,0), 0 2px 8px rgba(0,0,0,0.3); }
                    100% { box-shadow: 0 0 0 0 rgba(251,191,36,0), 0 2px 8px rgba(0,0,0,0.3); }
                }
                .mobile-pick-value-btn {
                    animation: pickValuePulse 1.8s ease-out infinite;
                }
                .mobile-pick-value-btn:active {
                    transform: scale(0.95) !important;
                    animation: none !important;
                }
            `}</style>

            {/* Persistent Event Banner — bottom-left to avoid overlapping centered action buttons.
                key={version} forces re-mount on every new event, re-triggering the animation. */}
            {eventBanner && (
                <div
                    key={eventBanner.version}
                    className="event-banner"
                    onClick={() => setEventBanner(null)}
                    title="Click to dismiss"
                    style={{
                        position: "fixed",
                        bottom: viewportWidth > 768 ? "110px" : "80px",
                        left: viewportWidth > 768 ? "16px" : "8px",
                        zIndex: 110,
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        padding: "8px 12px 8px 6px",
                        borderRadius: "12px",
                        maxWidth: viewportWidth > 768 ? "min(380px, calc(50vw - 24px))" : "calc(100vw - 16px)",
                        backdropFilter: "blur(16px)",
                        WebkitBackdropFilter: "blur(16px)",
                        boxShadow: "0 4px 24px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.06)",
                        animation: "bannerSlideIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) forwards",
                        backgroundColor: "rgba(15, 23, 42, 0.97)",
                        border: eventBanner.type === 'bluff'
                            ? "1px solid rgba(251, 191, 36, 0.4)"
                            : "1px solid rgba(56, 189, 248, 0.4)",
                        borderLeft: eventBanner.type === 'bluff'
                            ? "3px solid #fbbf24"
                            : "3px solid #38bdf8",
                        cursor: "pointer",
                        transition: "opacity 0.15s ease",
                        userSelect: "none",
                    }}
                >
                    {/* Category label */}
                    <div style={{
                        fontSize: "9px",
                        fontWeight: 800,
                        letterSpacing: "1.2px",
                        textTransform: "uppercase",
                        color: eventBanner.type === 'bluff' ? '#fbbf24' : '#38bdf8',
                        backgroundColor: eventBanner.type === 'bluff'
                            ? "rgba(251, 191, 36, 0.12)"
                            : "rgba(56, 189, 248, 0.12)",
                        padding: "3px 7px",
                        borderRadius: "5px",
                        flexShrink: 0,
                        pointerEvents: "none",
                        whiteSpace: "nowrap",
                    }}>
                        {eventBanner.type === 'bluff' ? 'Bluff' : 'All Passed'}
                    </div>

                    {/* Message text */}
                    <div style={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: "13px",
                        color: "rgba(255,255,255,0.9)",
                        lineHeight: 1.5,
                        textAlign: "left",
                        wordBreak: "break-word",
                        pointerEvents: "none",
                    }}>
                        {eventBanner.message}
                    </div>

                    {/* × as a visual affordance only — click on whole banner dismisses */}
                    <span style={{
                        color: "rgba(255,255,255,0.3)",
                        fontSize: "16px",
                        lineHeight: 1,
                        flexShrink: 0,
                        pointerEvents: "none",
                    }}>
                        ×
                    </span>
                </div>
            )}

            {/* Game Header with title and game info */}
            <GameHeader>
                <Link to="/" style={{ textDecoration: "none" }}>
                    <HeaderTitle style={{ cursor: "pointer" }}>Project <span style={{ color: "#fbbf24" }}>Bluff</span></HeaderTitle>
                </Link>
                <div style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "4px",
                    flex: 1,
                    textAlign: "center"
                }}>
                    {/* Row 1: Turn indicator + round value inline */}
                    <div style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "10px",
                        flexWrap: "wrap"
                    }}>
                        <div style={{
                            fontWeight: 700,
                            color: room.order[0].playerId === username ? "#10b981" : "#60a5fa",
                            fontSize: room.order[0].playerId === username ? "18px" : "16px",
                            transition: "all 0.3s ease",
                            whiteSpace: "nowrap"
                        }}>
                            {room.order[0].playerId === username
                                ? "It's your turn!"
                                : `${room.order[0].playerId}'s Turn`
                            }
                        </div>

                        {/* Round value: badge when locked, select/modal when picking, hint when waiting */}
                        {room.roundInfo.roundValue !== CardName.Joker ? (
                            <div style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "5px",
                                padding: "2px 10px",
                                backgroundColor: "rgba(96, 165, 250, 0.15)",
                                border: "1px solid rgba(96, 165, 250, 0.35)",
                                borderRadius: "20px",
                                fontSize: "12px",
                                color: "#93c5fd",
                                whiteSpace: "nowrap"
                            }}>
                                🃏 <strong>{CardName[room.roundInfo.roundValue]}s</strong>
                            </div>
                        ) : isTurn ? (
                            viewportWidth <= 768 ? (
                                // Mobile: tap to open bottom-sheet picker — pulsing to signal required action
                                <button
                                    className={getCardNameFromSelect(roundVal) === CardName.Joker ? "mobile-pick-value-btn" : ""}
                                    onClick={() => setShowMobileValuePicker(true)}
                                    style={{
                                        padding: "7px 14px",
                                        backgroundColor: getCardNameFromSelect(roundVal) === CardName.Joker
                                            ? "rgba(251,191,36,0.18)"
                                            : "rgba(96,165,250,0.2)",
                                        border: getCardNameFromSelect(roundVal) === CardName.Joker
                                            ? "2px solid rgba(251,191,36,0.7)"
                                            : "2px solid rgba(96,165,250,0.6)",
                                        borderRadius: "20px",
                                        color: getCardNameFromSelect(roundVal) === CardName.Joker ? "#fbbf24" : "#60a5fa",
                                        fontSize: "13px",
                                        fontWeight: 700,
                                        cursor: "pointer",
                                        whiteSpace: "nowrap",
                                        letterSpacing: "0.2px",
                                        transition: "transform 0.15s ease"
                                    }}
                                >
                                    {getCardNameFromSelect(roundVal) === CardName.Joker
                                        ? "▼ Tap to pick card value"
                                        : `🃏 ${allCardOptions.find(o => o.value === CardName[getCardNameFromSelect(roundVal)])?.label}s — change`
                                    }
                                </button>
                            ) : (
                                // Desktop: inline Select dropdown
                                <div style={{ minWidth: "150px", maxWidth: "200px" }}>
                                    <Select
                                        isSearchable={false}
                                        options={cardOptions}
                                        onChange={(selectedOption) => setRoundVal(selectedOption)}
                                        onKeyDown={(e: any) => e.stopPropagation()}
                                        value={allCardOptions.find((cardOption) => CardName[getCardNameFromSelect(roundVal)] === cardOption.value && cardOption.value !== 'Joker')}
                                        placeholder="Pick round value…"
                                        styles={{
                                            control: (base) => ({
                                                ...base,
                                                minHeight: "28px",
                                                height: "28px",
                                                fontSize: "12px",
                                                backgroundColor: "rgba(30,41,59,0.9)",
                                                borderColor: "rgba(96,165,250,0.5)",
                                                boxShadow: "none",
                                                cursor: "pointer",
                                                "&:hover": { borderColor: "#60a5fa" }
                                            }),
                                            valueContainer: (base) => ({ ...base, padding: "0 8px" }),
                                            indicatorsContainer: (base) => ({ ...base, height: "28px" }),
                                            dropdownIndicator: (base) => ({ ...base, padding: "2px 4px" }),
                                            singleValue: (base) => ({ ...base, color: "#ffffff" }),
                                            placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: "12px" }),
                                            menu: (base) => ({ ...base, backgroundColor: "rgba(30,41,59,0.98)", zIndex: 200 }),
                                            option: (base, state) => ({
                                                ...base,
                                                backgroundColor: state.isFocused ? "rgba(96,165,250,0.2)" : "transparent",
                                                color: "#ffffff",
                                                fontSize: "12px"
                                            })
                                        }}
                                    />
                                </div>
                            )
                        ) : (
                            <div style={{ fontSize: "11px", color: "#64748b", fontStyle: "italic", whiteSpace: "nowrap" }}>
                                New round
                            </div>
                        )}
                    </div>

                    {/* Row 2: Card count + last play info */}
                    <div style={{ fontSize: "12px", color: "rgba(255,255,255,0.9)" }}>
                        {renderInfo(room)}
                        {player !== undefined ? ` You have ${player.hand.deck.getCount()} card${player.hand.deck.getCount() > 1 ? 's': ''}.` : null}
                    </div>
                </div>

                {/* Mobile-only: compact horizontal player strip — order is static, highlight moves */}
                <MobilePlayerStrip>
                    {(staticPlayerOrder.length > 0 ? staticPlayerOrder : room.order.map(p => p.playerId)).map((playerId) => {
                        const p = room.order.find(o => o.playerId === playerId)
                        if (!p) return null
                        const isCurrentTurn = room.order[0].playerId === playerId
                        const isMe = playerId === username
                        const count = p.hand.deck.getCount()
                        return (
                            <div
                                key={playerId}
                                style={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    gap: "4px",
                                    fontSize: "10px",
                                    color: isCurrentTurn ? "#60a5fa" : "#e2e8f0",
                                    fontWeight: isCurrentTurn ? 700 : 400,
                                    backgroundColor: isCurrentTurn ? "rgba(96,165,250,0.18)" : "rgba(255,255,255,0.06)",
                                    padding: "2px 8px",
                                    borderRadius: "10px",
                                    border: isCurrentTurn ? "1px solid rgba(96,165,250,0.5)" : "1px solid rgba(255,255,255,0.1)",
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                    transition: "all 0.3s ease"
                                }}
                            >
                                {isMe ? "You" : playerId}: {count} {count === 1 ? "card" : "cards"}
                            </div>
                        )
                    })}
                </MobilePlayerStrip>
            </GameHeader>

            {/* Action Panel at Bottom with buttons */}
            <ActionPanel>
                <GameButton
                    disabled={!isTurn}
                    onClick={playSelectedCards}
                    title="Press Enter to play"
                >
                    Play {isTurn && numCardsSelected > 0 ? "(↵)" : ""}
                </GameButton>
                    <GameButton
                        backgroundColor="linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
                        disabled={room.cardSetFromRound.length === 0 || getLastPlayedCardSetWithCards(room).player === username || room.cardsInPlay.getCount() == 0 }
                        onClick={
                            ()=>{
                                bluffOnClick(room, player, gameId, username, setRoom, setBluffCalled)
                                    .then((roomAndMessage:any) => {
                                        if (roomAndMessage === undefined) {
                                            return
                                        }
                                        let r:Room = roomAndMessage["room"]
                                        let message:string = roomAndMessage["message"]
                                        if (isObjEqual(r, nullRoom)) {
                                            return
                                        }
                                        // Store for use in banner when caller decides show/hide
                                        bluffOutcomeMessage.current = message
                                        setBluffModalText(
                                            <div>
                                                <div>
                                                    {message}
                                                </div>
                                                <GameButton width="45%" onClick={async () => {
                                                    try {
                                                        await moveCards(gameId, true)
                                                        setBluffModalIsOpen(false)
                                                        // Show a brief banner — the card reveal modal shows full details
                                                        setEventBanner(prev => ({
                                                            type: 'bluff',
                                                            message: stripQuestion(bluffOutcomeMessage.current),
                                                            version: (prev?.version ?? 0) + 1
                                                        }))
                                                    } catch (error) {
                                                        console.error('Error showing deck:', error)
                                                        toast.error('Failed to process bluff result.')
                                                    }
                                                }}>
                                                    Show the deck
                                                </GameButton>
                                                <GameButton width="45%" onClick={async () => {
                                                    try {
                                                        await moveCards(gameId, false)
                                                        setBluffModalIsOpen(false)
                                                        // Banner is the only notification when deck is not shown
                                                        setEventBanner(prev => ({
                                                            type: 'bluff',
                                                            message: stripQuestion(bluffOutcomeMessage.current),
                                                            version: (prev?.version ?? 0) + 1
                                                        }))
                                                    } catch (error) {
                                                        console.error('Error hiding deck:', error)
                                                        toast.error('Failed to process bluff result.')
                                                    }
                                                }}>
                                                    Don't show the deck
                                                </GameButton>
                                            </div>
                                        )
                                        setBluffModalIsOpen(true)
                                    })
                            }
                        }
                    >
                        Call Bluff
                    </GameButton>
                    <GameButton
                        disabled={!isTurn || room.cardSetFromRound.length === 0}
                        onClick={async () => {
                            try {
                                await apiClient.passTurn(gameId, username)
                                // SSE will update the room state automatically
                            } catch (error) {
                                console.error('Error passing turn:', error)
                                toast.error('Failed to pass turn. Please try again.')
                            }
                        }}
                    >
                        Pass
                    </GameButton>
            </ActionPanel>

            {/* Player Order Panel */}
            <RivalOrderPanel>
                <div style={{ fontWeight: 700, marginBottom: 8, borderBottom: "1px solid rgba(255, 255, 255, 0.2)", paddingBottom: 6 }}>
                    Player Order
                </div>
                {room.order.map((player, index) => {
                    const cardCount = player.hand.deck.getCount()
                    const isCurrentPlayer = index === 0
                    const isUser = player.playerId === username

                    // Create mini card fan visualization
                    const maxCardsToShow = Math.min(cardCount, 7) // Show max 7 cards in mini view
                    const cards = []
                    for (let i = 0; i < maxCardsToShow; i++) {
                        const angle = (i - (maxCardsToShow - 1) / 2) * 3 // Smaller angle for mini view
                        cards.push(
                            <div
                                key={i}
                                style={{
                                    position: "absolute",
                                    width: "12px",
                                    height: "18px",
                                    backgroundColor: isUser ? "rgba(96, 165, 250, 0.4)" : "rgba(255, 255, 255, 0.3)",
                                    border: "1px solid rgba(255, 255, 255, 0.5)",
                                    borderRadius: "2px",
                                    transform: `translateX(${i * 4}px) rotate(${angle}deg)`,
                                    transformOrigin: "bottom center",
                                    left: 0
                                }}
                            />
                        )
                    }

                    return (
                        <div
                            key={player.playerId}
                            style={{
                                padding: "8px 0",
                                color: isCurrentPlayer ? "#60a5fa" : "#ffffff",
                                fontWeight: isCurrentPlayer ? 700 : 500,
                                borderLeft: isCurrentPlayer ? "3px solid #60a5fa" : "none",
                                paddingLeft: isCurrentPlayer ? "8px" : "0",
                                marginLeft: isCurrentPlayer ? "-8px" : "0"
                            }}
                        >
                            <div style={{ marginBottom: 6, fontSize: "0.95em" }}>
                                {index + 1}. {isUser ? "You" : player.playerId}
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                                <div style={{ position: "relative", height: "22px", width: `${Math.min(cardCount * 4 + 12, 40)}px` }}>
                                    {cards}
                                </div>
                                <span style={{ fontSize: "0.85em", opacity: 0.7 }}>
                                    {cardCount} card{cardCount !== 1 ? 's' : ''}
                                </span>
                            </div>
                        </div>
                    )
                })}
            </RivalOrderPanel>

            {/* Selected Cards Table */}
            {numCardsSelected > 0 && (
                <SelectedCardsTable>
                    <div style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color: "#60a5fa",
                        marginBottom: "8px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        textAlign: "center"
                    }}>
                        Selected ({numCardsSelected})
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "stretch", width: "100%" }}>
                        {renderSelectedCards(cards, selectedCards, setCards, setSelectedCards, numCardsSelected, setNumCardsSelected)}
                    </div>
                </SelectedCardsTable>
            )}

            {/* Modals */}
            <Modal isOpen={room.showCardSetFromRound && !controlBluffModalIsOpen} buttonOnClick={()=>{setControlBluffModalIsOpen(true)}}>
                {renderBluffCards(room.cardSetFromLastRound)}
            </Modal>
            <Modal isOpen={bluffModalIsOpen} buttonOnClick={()=>{}} showX={false}>
                {bluffModalText}
            </Modal>

            {/* Mobile Value Picker — bottom-sheet modal */}
            {showMobileValuePicker && (
                <div
                    style={{
                        position: "fixed",
                        inset: 0,
                        zIndex: 600,
                        backgroundColor: "rgba(0,0,0,0.65)",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end"
                    }}
                    onClick={() => setShowMobileValuePicker(false)}
                >
                    <div
                        style={{
                            backgroundColor: "rgba(15, 23, 42, 0.99)",
                            backdropFilter: "blur(20px)",
                            WebkitBackdropFilter: "blur(20px)",
                            borderRadius: "16px 16px 0 0",
                            padding: "20px 16px 28px",
                            boxShadow: "0 -8px 32px rgba(0,0,0,0.6)",
                            border: "1px solid rgba(255,255,255,0.1)"
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div style={{
                            fontSize: "15px",
                            fontWeight: 700,
                            color: "#60a5fa",
                            marginBottom: "16px",
                            textAlign: "center",
                            letterSpacing: "0.3px"
                        }}>
                            Pick Round Card Value
                        </div>
                        <div style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(4, 1fr)",
                            gap: "10px",
                            marginBottom: "14px"
                        }}>
                            {cardOptions.map((opt) => {
                                const isSelected = roundVal && (roundVal as any).value === opt.value
                                return (
                                    <button
                                        key={opt.value}
                                        onClick={() => {
                                            setRoundVal(opt)
                                            setShowMobileValuePicker(false)
                                        }}
                                        style={{
                                            padding: "13px 4px",
                                            backgroundColor: isSelected
                                                ? "rgba(96, 165, 250, 0.3)"
                                                : "rgba(255,255,255,0.07)",
                                            border: isSelected
                                                ? "2px solid #60a5fa"
                                                : "1px solid rgba(255,255,255,0.15)",
                                            borderRadius: "10px",
                                            color: "#ffffff",
                                            fontSize: "14px",
                                            fontWeight: isSelected ? 700 : 500,
                                            cursor: "pointer",
                                            transition: "all 0.15s ease"
                                        }}
                                    >
                                        {opt.label}
                                    </button>
                                )
                            })}
                        </div>
                        <button
                            onClick={() => setShowMobileValuePicker(false)}
                            style={{
                                width: "100%",
                                padding: "11px",
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: "1px solid rgba(255,255,255,0.1)",
                                borderRadius: "10px",
                                color: "rgba(255,255,255,0.55)",
                                fontSize: "14px",
                                cursor: "pointer"
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}

            {/* Card Hand - Only showing unselected cards */}
            <CardInfo>
                <div style={{
                    position: "relative",
                    width: "100%",
                    height: viewportWidth > 768 ? "clamp(200px, calc(100vh - 240px), 420px)" : "100%"
                }}>
                    {renderCards(
                        cards,
                        selectedCards,
                        setCards,
                        setSelectedCards,
                        numCardsSelected,
                        setNumCardsSelected,
                        cardToIdMap.current
                    )}
                </div>
            </CardInfo>

        </Background>
    )
}

export default Game